import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import { Plus, X, Copy, Trash2, MessageSquare, BookOpen, FileText, Save, Upload, Send } from "lucide-react";

const SAVED_BADGE = (when) => when ? `Saved · ${new Date(when).toLocaleString()}` : "Not saved yet";

export function InterviewTemplatesPage() {
  const [items, setItems] = useState([]);
  const [editor, setEditor] = useState(null); // null | "new" | object
  const [tab, setTab] = useState("active"); // "active" | "drafts"

  const load = async () => {
    const { data } = await api.get("/admin/interview-templates");
    // legacy rows without status => treat as active. active!==false respected for legacy soft-deletes.
    setItems(data.filter((t) => t.active !== false));
  };
  useEffect(() => { load(); }, []);

  const isDraft = (t) => (t.status || "active") === "draft";
  const activeItems = useMemo(() => items.filter((t) => !isDraft(t)), [items]);
  const draftItems  = useMemo(() => items.filter((t) =>  isDraft(t)), [items]);

  const clone = async (t) => {
    try { await api.post(`/admin/interview-templates/${t.id}/clone`); toast.success("Cloned"); load(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (t) => {
    const msg = isDraft(t)
      ? `Delete draft "${t.name || "Untitled"}"? This cannot be undone.`
      : `Delete "${t.name}"?`;
    if (!window.confirm(msg)) return;
    try { await api.delete(`/admin/interview-templates/${t.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };
  const publish = async (t) => {
    try {
      await api.post(`/admin/interview-templates/${t.id}/publish`);
      toast.success("Draft published");
      setTab("active");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish failed");
    }
  };

  const visible = tab === "active" ? activeItems : draftItems;

  return (
    <div className="fade-up">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-eyebrow mb-2">Recruitment</div>
          <h1 className="font-display text-4xl font-bold flex items-center gap-3"><BookOpen className="w-8 h-8" />Interview Templates</h1>
          <p className="text-[var(--cc-text-soft)] mt-1">Reusable competency frameworks with 1-5 scoring rubric. Save partial work as a draft and publish when ready.</p>
        </div>
        <button className="btn-primary" onClick={() => setEditor("new")} data-testid="new-template-button"><Plus className="w-4 h-4" /> New template</button>
      </div>

      <div className="flex gap-2 mb-5" data-testid="templates-tabs">
        <button
          className={`px-4 py-2 rounded-full text-sm font-medium transition ${tab === "active" ? "bg-[var(--cc-sage)] text-white" : "bg-[var(--cc-surface)] hover:bg-[var(--cc-surface-hover)]"}`}
          onClick={() => setTab("active")}
          data-testid="templates-tab-active"
        >
          <FileText className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Active Templates <span className="opacity-70">({activeItems.length})</span>
        </button>
        <button
          className={`px-4 py-2 rounded-full text-sm font-medium transition ${tab === "drafts" ? "bg-[var(--cc-sage)] text-white" : "bg-[var(--cc-surface)] hover:bg-[var(--cc-surface-hover)]"}`}
          onClick={() => setTab("drafts")}
          data-testid="templates-tab-drafts"
        >
          <Save className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          My Drafts <span className="opacity-70">({draftItems.length})</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 stagger" data-testid={`templates-list-${tab}`}>
        {visible.map((t) => (
          <div key={t.id} className="cc-card p-6" data-testid={`template-card-${t.id}`}>
            <div className="flex items-start justify-between">
              <div className="cursor-pointer flex-1" onClick={() => setEditor(t)}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="label-eyebrow">{(t.stage || "").replace("_", " ")}</span>
                  {isDraft(t) && <span className="pill pill-warning !text-[10px]" data-testid={`draft-badge-${t.id}`}>Draft</span>}
                </div>
                <div className="font-display text-xl font-bold mt-1">{t.name || <span className="italic opacity-60">Untitled draft</span>}</div>
                <div className="text-xs text-[var(--cc-text-soft)] mt-1">
                  {(t.questions?.length || 0)} questions · {(t.competencies?.length || 0)} competencies{isDraft(t) ? "" : ` · v${t.version}`}
                </div>
                <div className="text-[10px] text-[var(--cc-text-soft)] mt-1.5" data-testid={`template-saved-at-${t.id}`}>{SAVED_BADGE(t.saved_at || t.updated_at || t.created_at)}</div>
              </div>
              <div className="flex gap-1">
                {isDraft(t) && (
                  <button className="btn-ghost p-1.5" title="Publish draft" onClick={() => publish(t)} data-testid={`publish-template-${t.id}`}>
                    <Send className="w-4 h-4" />
                  </button>
                )}
                {!isDraft(t) && (
                  <button className="btn-ghost p-1.5" title="Clone" onClick={() => clone(t)} data-testid={`clone-template-${t.id}`}><Copy className="w-4 h-4" /></button>
                )}
                <button className="btn-ghost p-1.5" title="Delete" onClick={() => remove(t)} data-testid={`delete-template-${t.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1">
              {(t.competencies || []).map((c) => <span key={c} className="pill pill-sage !text-[10px]">{c}</span>)}
            </div>
          </div>
        ))}
        {visible.length === 0 && (
          <div className="col-span-2 cc-card p-8 text-center text-[var(--cc-text-soft)]" data-testid={`empty-${tab}`}>
            {tab === "drafts" ? "No drafts yet — click \"Save Draft\" while editing a template to keep your work-in-progress." : "No active templates yet — create one to get started."}
          </div>
        )}
      </div>

      {editor && <TemplateEditor item={editor === "new" ? null : editor} onClose={() => setEditor(null)} onSaved={(savedDoc) => { load(); if (savedDoc?.status === "draft") { setTab("drafts"); } }} />}
    </div>
  );
}

function TemplateEditor({ item, onClose, onSaved }) {
  // `current` holds the persisted backing row (so subsequent saves PATCH it).
  // For brand-new templates we start with null; the first save creates the row.
  const [current, setCurrent] = useState(item || null);

  const [form, setForm] = useState(() => current ? {
    name: current.name || "", stage: current.stage || "phone_screen",
    competencies: (current.competencies || []).join(", "),
    rubric: current.rubric || { "1": "Does Not Meet", "2": "Below", "3": "Meets", "4": "Exceeds", "5": "Far Exceeds" },
    questions: current.questions || [],
  } : {
    name: "", stage: "phone_screen", competencies: "",
    rubric: { "1": "Does Not Meet", "2": "Below Expectations", "3": "Meets", "4": "Exceeds", "5": "Far Exceeds" },
    questions: [],
  });
  const [busy, setBusy] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(current?.saved_at || current?.updated_at || current?.created_at || null);

  const status = current?.status || "active";
  const isDraft = status === "draft";
  const isFormEmpty = !form.name.trim() && form.questions.length === 0 && !form.competencies.trim();

  const addQ = () => setForm({ ...form, questions: [...form.questions, { text: "", competency: form.competencies.split(",")[0]?.trim() || "", guidance: "" }] });
  const upQ = (i, k, v) => {
    const qs = [...form.questions]; qs[i] = { ...qs[i], [k]: v }; setForm({ ...form, questions: qs });
  };
  const rmQ = (i) => setForm({ ...form, questions: form.questions.filter((_, idx) => idx !== i) });

  const buildPayload = (statusValue) => ({
    name: form.name, stage: form.stage,
    competencies: form.competencies.split(",").map((c) => c.trim()).filter(Boolean),
    rubric: form.rubric, questions: form.questions,
    status: statusValue,
  });

  const doSave = async (statusValue) => {
    if (statusValue === "draft" && isFormEmpty) {
      return toast.error("Add a template name or some content before saving as draft");
    }
    if (statusValue === "draft" && !form.name.trim()) {
      return toast.error("Template name is required");
    }
    if (statusValue === "active") {
      if (!form.name.trim()) return toast.error("Name is required");
      if (form.questions.length < 5) return toast.error("Add at least 5 questions to publish");
    }
    setBusy(true);
    try {
      const payload = buildPayload(statusValue);
      let row;
      if (current?.id) {
        const { data } = await api.patch(`/admin/interview-templates/${current.id}`, payload);
        row = { ...current, ...payload, ...data };
      } else {
        const { data } = await api.post("/admin/interview-templates", payload);
        row = data;
      }
      setCurrent(row);
      setLastSavedAt(row.saved_at || new Date().toISOString());
      toast.success(statusValue === "draft" ? "Template saved as draft" : "Saved");
      onSaved(row);
      if (statusValue === "active") onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const publish = async () => {
    if (!current?.id) {
      // first save the row, then publish
      await doSave("draft");
    }
    if (form.questions.length < 5) return toast.error("Add at least 5 questions to publish");
    if (!form.name.trim()) return toast.error("Template name is required to publish");
    setBusy(true);
    try {
      // ensure latest content is persisted first (so publish sees ≥5 questions)
      await api.patch(`/admin/interview-templates/${current.id}`, buildPayload("draft"));
      await api.post(`/admin/interview-templates/${current.id}/publish`);
      toast.success("Draft published");
      onSaved({ ...current, status: "active" });
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish failed");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} testId="template-editor" widthClass="max-w-3xl">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-display text-2xl font-bold">{current?.id ? (isDraft ? "Edit draft" : "Edit template") : "New interview template"}</h2>
          <div className="flex items-center gap-2 mt-1">
            {isDraft && <span className="pill pill-warning !text-[10px]">Draft</span>}
            <span className="text-[11px] text-[var(--cc-text-soft)]" data-testid="editor-saved-at">{SAVED_BADGE(lastSavedAt)}</span>
          </div>
        </div>
        <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div><label className="label-eyebrow block mb-1">Name</label>
            <input className="cc-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="template-name-input" /></div>
          <div><label className="label-eyebrow block mb-1">Stage</label>
            <select className="cc-select" value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} data-testid="template-stage-select">
              <option value="phone_screen">Phone Screen</option>
              <option value="in_person">In-person Interview</option>
              <option value="panel">Panel Interview</option>
            </select></div>
          <div className="col-span-2"><label className="label-eyebrow block mb-1">Competencies (comma-separated)</label>
            <input className="cc-input" value={form.competencies} onChange={(e) => setForm({ ...form, competencies: e.target.value })} placeholder="Communication, Safeguarding, Reliability…" data-testid="template-competencies-input" /></div>
        </div>

        <div className="mb-2 flex items-center justify-between">
          <div className="label-eyebrow">Questions ({form.questions.length})</div>
          <button className="btn-secondary text-xs py-1" onClick={addQ} data-testid="add-question-button">+ Add question</button>
        </div>
        <div className="space-y-2 mb-5">
          {form.questions.map((q, i) => (
            <div key={i} className="border border-[var(--cc-border)] rounded-lg p-3 space-y-2" data-testid={`question-row-${i}`}>
              <div className="flex justify-between gap-2">
                <input className="cc-input flex-1" placeholder="Question text" value={q.text} onChange={(e) => upQ(i, "text", e.target.value)} />
                <button className="btn-ghost p-1.5" onClick={() => rmQ(i)}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input className="cc-input" placeholder="Competency tag" value={q.competency} onChange={(e) => upQ(i, "competency", e.target.value)} />
                <input className="cc-input" placeholder="Interviewer guidance (optional)" value={q.guidance || ""} onChange={(e) => upQ(i, "guidance", e.target.value)} />
              </div>
            </div>
          ))}
        </div>

        <div className="label-eyebrow mb-2">Rubric (1-5)</div>
        <div className="grid grid-cols-5 gap-2 mb-5">
          {["1", "2", "3", "4", "5"].map((k) => (
            <div key={k} className="text-center">
              <div className="rating-pill selected mx-auto mb-1">{k}</div>
              <input className="cc-input text-xs text-center" value={form.rubric[k] || ""} onChange={(e) => setForm({ ...form, rubric: { ...form.rubric, [k]: e.target.value } })} />
            </div>
          ))}
        </div>

        <div className="flex justify-between gap-2">
          <button
            className="btn-secondary inline-flex items-center gap-1.5"
            onClick={() => doSave("draft")}
            disabled={busy || isFormEmpty}
            data-testid="save-draft-button"
            title={isFormEmpty ? "Add a name or content first" : "Save current state as draft"}
          >
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save Draft"}
          </button>
          <div className="flex gap-2">
            <button className="btn-ghost" onClick={onClose}>Cancel</button>
            {isDraft ? (
              <button className="btn-primary inline-flex items-center gap-1.5" onClick={publish} disabled={busy} data-testid="publish-draft-button">
                <Upload className="w-4 h-4" /> {busy ? "Publishing…" : "Publish Draft"}
              </button>
            ) : (
              <button className="btn-primary" onClick={() => doSave("active")} disabled={busy} data-testid="save-template-button">{busy ? "Saving…" : "Save"}</button>
            )}
          </div>
        </div>
    </Modal>
  );
}

// =============== SMS LOG ===============
export function AdminSmsLogPage() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/sms-log").then(({ data }) => setRows(data)); }, []);
  return (
    <div className="fade-up">
      <div className="mb-6">
        <div className="label-eyebrow mb-2">Communications</div>
        <h1 className="font-display text-4xl font-bold flex items-center gap-3"><MessageSquare className="w-8 h-8" />SMS Log</h1>
        <p className="text-[var(--cc-text-soft)] mt-1">Every SMS sent via Voodoo SMS — UK numbers only, max 160 chars.</p>
      </div>
      <div className="cc-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-5 py-3 label-eyebrow">When</th>
              <th className="px-5 py-3 label-eyebrow">To</th>
              <th className="px-5 py-3 label-eyebrow">Message</th>
              <th className="px-5 py-3 label-eyebrow">Tag</th>
              <th className="px-5 py-3 label-eyebrow">Provider</th>
              <th className="px-5 py-3 label-eyebrow">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[var(--cc-border)]" data-testid={`sms-log-${r.id}`}>
                <td className="px-5 py-3 text-xs text-[var(--cc-text-soft)] whitespace-nowrap">{new Date(r.sent_at).toLocaleString()}</td>
                <td className="px-5 py-3 font-mono text-xs">{r.to_phone_normalized || r.to_phone}</td>
                <td className="px-5 py-3 max-w-md truncate">{r.message}</td>
                <td className="px-5 py-3"><span className="pill pill-info">{r.tag}</span></td>
                <td className="px-5 py-3"><span className={`pill ${r.provider === "voodoo_sms" ? "pill-sage" : "pill-warning"}`}>{r.provider}</span></td>
                <td className="px-5 py-3"><span className={`pill ${r.status === "sent" ? "pill-success" : r.status === "failed" ? "pill-danger" : "pill-warning"}`}>{r.status}</span>{r.error && <div className="text-[10px] text-[var(--cc-accent)] mt-1">{typeof r.error === "string" ? r.error : (r.error?.msg || r.error?.message || JSON.stringify(r.error))}</div>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="6" className="text-center py-10 text-[var(--cc-text-soft)]">No SMS sent yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
